import { createContext, PropsWithChildren, useState } from 'react'
import { DEFAULT_SIDEBAR_PANEL } from 'enums/sidebar'

export type SidebarContextType = {
  sidebarIsVisible?: boolean
  toggleSidebarIsVisible: () => void
  visibleNavList: {
    previous?: string[]
    current: string
  }
  updateVisibleNavList: (desiredNavList: string | string[]) => void
  returnToPreviousNavList: () => void
}

export const SidebarContext = createContext<SidebarContextType>({
  sidebarIsVisible: false,
  toggleSidebarIsVisible: () => {
    throw new Error('useSidebar must be used within SidebarProvider')
  },
  visibleNavList: {
    current: DEFAULT_SIDEBAR_PANEL
  },
  updateVisibleNavList: () => {
    throw new Error('useSidebar must be used within SidebarProvider')
  },
  returnToPreviousNavList: () => {
    throw new Error('useSidebar must be used within SidebarProvider')
  }
})

export type SidebarProviderProps = PropsWithChildren<unknown>

/**
 * Use to provide information of which layers of the navigation are visible.
 *
 * The navigation uses a recursive function to generate the sub navigations, this provider helps to
 * eliminate prop drilling while switching views between the different levels of the navigation.
 */
const SidebarProvider = ({ children }: SidebarProviderProps) => {
  const [sidebarIsVisible, setSidebarIsVisible] =
    useState<SidebarContextType['sidebarIsVisible']>()

  const toggleSidebarIsVisible = () => {
    setSidebarIsVisible(!sidebarIsVisible)
  }

  /**
   * For maintaining state on what navlist is currently and previously visible.
   * Used for determining what css should be used for each navlist - e.g. if the navlist is previous then
   * it should transition right to left if it is the current then it should transition in left to right.
   */
  const [visibleNavList, setVisibleNavList] = useState<
    SidebarContextType['visibleNavList']
  >({
    previous: undefined,
    current: DEFAULT_SIDEBAR_PANEL
  })

  /**
   * This is to make its way into all of the NavList. It updates the visibleNavList's state which can
   * be seen across the whole sidebar.
   *
   * The prop "desiredNavList" accepts a string or array of string, see below their use cases:
   * - String: Give the desired NavList's name and allow the rest of the path be determined by the
   * function. Used by clicking on a NavItem.
   * - Array of string: Give the sidebar's path to the desired NavList. Used for setting the initial
   * state.
   */
  const updateVisibleNavList: SidebarContextType['updateVisibleNavList'] =
    desiredNavList => {
      let previous: SidebarContextType['visibleNavList']['previous']
      let current: SidebarContextType['visibleNavList']['current']

      if (Array.isArray(desiredNavList)) {
        const hasPrevious = desiredNavList.length > 1
        previous = hasPrevious ? desiredNavList : undefined
        current = desiredNavList.pop() ?? DEFAULT_SIDEBAR_PANEL
      } else {
        const availablePrevious = [
          ...(visibleNavList?.previous ?? []),
          visibleNavList.current
        ]
        previous =
          desiredNavList !== DEFAULT_SIDEBAR_PANEL
            ? availablePrevious
            : undefined
        current = desiredNavList
      }

      setVisibleNavList({
        previous,
        current
      })
    }

  const returnToPreviousNavList = () => {
    const previous = visibleNavList?.previous ?? []

    setVisibleNavList({
      previous: previous?.filter(
        item => item !== previous[previous.length - 1]
      ),
      current: previous[previous.length - 1]
    })
  }

  return (
    <SidebarContext.Provider
      value={{
        sidebarIsVisible,
        toggleSidebarIsVisible,
        visibleNavList,
        updateVisibleNavList,
        returnToPreviousNavList
      }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

export default SidebarProvider
